import {
  BookOpenCheck,
  Braces,
  Clock3,
  FileClock,
  FileStack,
  GitCompareArrows,
  ListTree,
  MessageSquareText,
  Sparkles,
} from 'lucide-react';
import {
  ChoiceCards,
  Segmented,
  SettingsCard,
  SettingsPageHeader,
  SettingsRow,
  SettingsScopeNote,
  SystemRule,
  Toggle,
} from './SettingsPrimitives';
import PreferenceSyncNotice from './PreferenceSyncNotice';
import usePreferenceNamespace from './usePreferenceNamespace';
import { useTeamBrand } from '../../branding/TeamBrandContext';

const WORKFLOW_OPTIONS = [
  {
    value: 'light',
    label: '轻量',
    description: '灵感来了就快写',
    icon: Sparkles,
  },
  {
    value: 'standard',
    label: '标准',
    description: '先规划，再写作，再检查',
    icon: BookOpenCheck,
  },
  {
    value: 'deep',
    label: '深度',
    description: '长剧慢慢磨，多查几遍',
    icon: Braces,
  },
];

const WRITING_DEFAULTS = {
  autoSave: true,
  saveDelay: 3,
  maxTabs: 6,
  workflow: 'standard',
  planMode: false,
  collapseBody: true,
  snapshots: true,
  snapshotInterval: 10,
};

export default function WritingSettingsPanel({ preview = false, english = false }) {
  const brand = useTeamBrand();
  const {
    values,
    status,
    statusLabel,
    updatePreference,
    retry,
  } = usePreferenceNamespace('writing', WRITING_DEFAULTS, { enabled: !preview });
  const {
    autoSave,
    saveDelay,
    maxTabs,
    workflow,
    planMode,
    collapseBody,
    snapshots,
    snapshotInterval,
  } = values;

  return (
    <div className={`account-content__stack creation-settings-page${status === 'loading' ? ' is-loading' : ''}`}>
      <SettingsPageHeader
        eyebrow="WRITING"
        title={english ? 'Writing Settings' : '写作设置'}
        description={english ? 'Tune the writing room to your rhythm, keep ideas flowing, and make long-form work steadier.' : '把写作台调成你顺手的样子，让灵感不断线，长篇也写得稳。'}
        status={english ? ({ preview: 'Personal settings', loading: 'Loading', ready: 'Synced', saving: 'Saving', saved: 'Saved', conflict: 'Review needed', error: '保存失败' }[status]) : statusLabel}
      />
      <PreferenceSyncNotice
        preview={preview}
        status={status}
        onRetry={retry}
        previewText={english ? 'Adjust freely—these choices will not disrupt the project you are writing.' : '放心调一调，当前选择不会打乱你正在写的项目。'}
        english={english}
      />

      <SettingsCard
        eyebrow="SAVE & WORKSPACE"
        title={english ? 'Saving & Workspace' : '保存与工作台'}
        description={english ? 'Keep your work safely saved and move between episodes smoothly.' : '让写作自动收好，也让多集切换更顺手。'}
        aside={english ? 'Personal default' : '个人默认'}
      >
        <SettingsRow
          icon={FileClock}
          title={english ? 'Autosave' : '自动保存'}
          description={english ? 'Save after script edits. If saving fails, keep a local draft and show a persistent warning.' : '正文修改后自动保存；失败时保留本地草稿并常驻提示。'}
        >
          <Toggle checked={autoSave} onChange={value => updatePreference('autoSave', value)} label={english ? 'Autosave' : '自动保存'} />
        </SettingsRow>
        <SettingsRow
          icon={Clock3}
          title={english ? 'Save after inactivity' : '停笔多久后保存'}
          description={english ? 'Wait until you pause instead of interrupting every sentence.' : '不用每句话都打断，停下来再替你收好。'}
        >
          <Segmented
            label={english ? 'Autosave delay' : '自动保存延迟'}
            value={saveDelay}
            options={[1, 3, 5, 10].map(value => ({ value, label: `${value} ${english ? 'sec' : '秒'}` }))}
            onChange={value => updatePreference('saveDelay', value)}
            disabled={!autoSave}
          />
        </SettingsRow>
        <SettingsRow
          icon={FileStack}
          title={english ? 'Episodes open at once' : '同时打开几集'}
          description={english ? 'When too many are open, close the oldest saved episode first.' : '打开太多时，先收起最久没看且已经保存的那一集。'}
        >
          <Segmented
            label={english ? 'Maximum open tabs' : '最大打开标签数'}
            value={maxTabs}
            options={[4, 6, 8, 12]}
            onChange={value => updatePreference('maxTabs', value)}
          />
        </SettingsRow>
        <SettingsScopeNote>
          {english ? 'Episode groups and total episode count belong to the project. This page only controls your personal writing habits.' : '章节分组和全剧集数跟着项目走，这里只管你自己的写作习惯。'}
        </SettingsScopeNote>
      </SettingsCard>

      <SettingsCard
        eyebrow="WRITING WORKFLOW"
        title={english ? 'Writing Workflow' : '创作工作流'}
        description={english ? 'Choose a creative rhythm. You can still override it before each task.' : '选一个合适的创作节奏，每次开写前也能临时调整。'}
        aside={english ? 'Personal default' : '个人默认'}
      >
        <ChoiceCards
          label={english ? 'Workflow depth' : '写作工作流强度'}
          value={workflow}
          options={english ? WORKFLOW_OPTIONS.map((option, index) => ({ ...option, label: ['Light', 'Standard', 'Deep'][index], description: ['Write quickly when inspiration strikes', 'Plan, write, then review', 'More checks for long-form stories'][index] })) : WORKFLOW_OPTIONS}
          onChange={value => updatePreference('workflow', value)}
        />
        <SettingsRow
          icon={ListTree}
          title={english ? 'Plan before execution by default' : '默认先规划再执行'}
          description={english ? 'For complex tasks, show the plan first and write after you approve it.' : '碰到复杂任务，先把计划给你看，点头后再动笔。'}
        >
          <Toggle checked={planMode} onChange={value => updatePreference('planMode', value)} label={english ? 'Plan before execution by default' : '默认先规划再执行'} />
        </SettingsRow>
        <SettingsRow
          icon={MessageSquareText}
          title={english ? 'Show summaries in chat' : '对话里只看摘要'}
          description={english ? 'Keep long drafts out of chat; full content is written into the editor.' : '少刷长正文，完整内容会好好写进编辑器。'}
        >
          <Toggle checked={collapseBody} onChange={value => updatePreference('collapseBody', value)} label={english ? 'Collapse full Agent drafts' : '折叠 Agent 完整正文'} />
        </SettingsRow>
      </SettingsCard>

      <SettingsCard
        eyebrow="VERSION & REVIEW"
        title={english ? 'Versions & Recovery' : '版本与恢复'}
        description={english ? 'Keep recoverable versions so you can safely return after a bad edit.' : '定期留下可恢复版本，改坏了也能安心回头。'}
        aside={english ? 'Personal default' : '个人默认'}
      >
        <SettingsRow
          icon={GitCompareArrows}
          title={english ? 'Automatic snapshots' : '自动留版本'}
          description={english ? 'Save a recoverable version at regular intervals.' : '隔一段时间留个可恢复版本，改坏了也能回头。'}
        >
          <Toggle checked={snapshots} onChange={value => updatePreference('snapshots', value)} label={english ? 'Automatic snapshots' : '自动版本快照'} />
        </SettingsRow>
        <SettingsRow
          icon={Clock3}
          title={english ? 'Snapshot interval' : '多久留一个版本'}
          description={english ? 'Continuous edits are grouped instead of creating a version for every keystroke.' : '连续修改会合在一起，不会每按一次键就多一个版本。'}
        >
          <Segmented
            label={english ? 'Snapshot interval' : '自动版本快照间隔'}
            value={snapshotInterval}
            options={[5, 10, 20, 30].map(value => ({ value, label: `${value} ${english ? 'min' : '分'}` }))}
            onChange={value => updatePreference('snapshotInterval', value)}
            disabled={!snapshots}
          />
        </SettingsRow>
        <SettingsScopeNote>
          {english ? 'Script review lives beside your project library. Choose a platform project, paste text, or upload a file whenever you want a fresh check.' : '剧本审核就在项目库旁边；需要时可以选择平台剧本、粘贴文本或上传文件，不用在个人设置里找开关。'}
        </SettingsScopeNote>
      </SettingsCard>

      <SettingsCard
        eyebrow="STORY SAFETY"
        title={english ? 'Story Safety' : '安心写作'}
        description={english ? 'These three safeguards keep protecting your story.' : '这三道保护会一直替你守着故事。'}
        aside={english ? 'Always on' : '始终开启'}
      >
        <div className="creation-system-rules">
          <SystemRule
            title={english ? 'Keep continuity intact' : '前后剧情不打架'}
            description={english ? 'Check character states, established facts, and key reveals before updating the official draft.' : '写入正式稿前，检查人物状态、前后事实和关键揭晓。'}
            status={english ? 'Automatic' : '自动守护'}
          />
          <SystemRule
            title={english ? 'Keep problem drafts separate' : '问题稿先放一边'}
            description={english ? 'Drafts that fail checks stay in Candidates and never silently overwrite the official version.' : '没通过检查的版本留在候选区，不会悄悄改掉正式稿。'}
            status={english ? 'No overwrite' : '不会覆盖'}
          />
          <SystemRule
            title={english ? 'You decide what becomes canon' : '故事由你拍板'}
            description={english ? 'New facts affect later episodes only after you confirm them.' : '新事实只有经过你确认，才会影响后面的剧情。'}
            status={english ? 'You confirm' : '由你确认'}
          />
        </div>
        <SettingsScopeNote>
          {brand.text(english ? 'Xiaoying handles the underlying workflow. You only decide how the story should be written.' : '底层运行由小影自动打理，你只需要决定故事怎么写。')}
        </SettingsScopeNote>
      </SettingsCard>
    </div>
  );
}
